import React from 'react';
import Stack from '@mui/joy/Stack';
import FormHelperText from '@mui/joy/FormHelperText';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Radio from '@mui/joy/Radio';
import Button from '@mui/joy/Button';
import RadioGroup from '@mui/joy/RadioGroup';
import ListDivider from '@mui/joy/ListDivider';
import Tooltip from '@mui/joy/Tooltip';
import { Typography } from '@mui/joy';


export type FancyRadioType = {
    radio: RadioType;
    options: OptionType[];
};

export type RadioType = {
    label: string;
    name: string;
    value: string;
    setValue: (s: string) => void;
    helper: string;
    required?: boolean;
    icon?: JSX.Element;
};

export type OptionType = {
    value: string;
    text: string;
    helper: string;
    icon: JSX.Element;
    recommended?: boolean;
    disabled?: boolean;
};

type _RadioOptionType = {
    item: OptionType;
    index: number;
    onOptionClick: (s: string, d?: boolean) => void;
    expanded: boolean;
};

function RadioOption({ item, onOptionClick, expanded, index }: _RadioOptionType) {

    const hinted = index < 1;

    return (
        <Tooltip
            size="sm"
            title={item.text + ": " + item.helper}
            placement="bottom"
            variant="soft"
            arrow >
            <>
                {index !== 0 && <ListDivider />}
                <ListItem key={item.value}
                     sx={{cursor: "pointer"}}
                    onClick={() => { onOptionClick(item.value, item.disabled) }}
                >
                    <ListItemDecorator>
                        {item.icon}
                    </ListItemDecorator>

                    { hinted || expanded ?
                    <Radio
                        overlay
                        value={item.value}
                        disabled={item.disabled}
                        label={(expanded || hinted ? item.text : '')}
                        slotProps={{ input: { 'aria-describedby': item.value + '-helper-text' } }}
                        onClick={() => { onOptionClick(item.value, item.disabled) }} /> :
                        <Typography >{item.text}</Typography> }
                    {expanded &&
                        <FormHelperText id={item.value + "-helper-text"}>{item.helper}</FormHelperText>}
                </ListItem>
            </>
        </Tooltip>
    );
}

function FancyRadio({ radio, options }: FancyRadioType) {

    const [expanded, setExpanded] = React.useState<boolean>(false);

    if (!radio) {
        return <em>dev-error: No radio</em>
    }
    if (!options) {
        return <em>dev-error: No options</em>
    }

    function onOptionClick(value: string, isOptionDisabled = false) {
        // expand the list either way
        setExpanded(true);
        // set the value if it's not disabled
        if(!isOptionDisabled) {
            // console.log("setting %o value to", radio, value);
            radio.setValue(value);
        }
    }

    // sort the options so that the selected one is first
    if (!expanded) {
        options.sort((a, b) => {
            if (a.value === radio.value) {
                return -1;
            }
            if (b.value === radio.value) {
                return 1;
            }
            return 0;
        });
    }


    return (
        <Stack sx={{ width: "100%" }}>
            <FormControl>
                <FormLabel>{radio.icon} {radio.label}</FormLabel>
                <FormHelperText>{radio.helper}</FormHelperText>

                <RadioGroup
                    aria-label={radio.label}
                    name={radio.name + "-group"}
                    defaultValue={radio.value}
                    value={radio.value}>

                    <List
                        orientation={expanded ? "vertical" : "horizontal"}
                        variant="outlined"

                        sx={{
                            p: 1,
                            overflowX: "hidden",
                            overflowY: "hidden",
                            minWidth: 240,
                            '--List-gap': '0px',
                            // '--ListItem-paddingY': '1rem',
                            '--List-radius': '8px',
                            '--ListItemDecorator-size': '32px',
                        }}
                    >
                        {options.map((item, index) =>
                            <RadioOption
                                key={item.value}
                                item={item}
                                index={index}
                                expanded={expanded}
                                onOptionClick={onOptionClick} />
                        )}
                    </List>
                </RadioGroup>
            </FormControl>
            {expanded &&
                <div style={{ textAlign: "center" }}>
                    <Button
                        variant="plain"
                        onClick={() => { setExpanded(false) }}
                        sx={{ mt: 2, mb: 2 }}>close</Button>
                </div>}
        </Stack>
    );
}

export default FancyRadio;
